import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import createEventApi from 'api/eventOffset';
import createFlightApi from 'api/flightOffset';
import createOrderApi from 'api/order';
import { OrderItem } from 'api/order';
import { appContextSelector } from 'modules/app/appSlice';
import { defaultPortfolioSelector } from 'modules/projects/projectsSlice';
import { syncAttribution } from 'modules/user/attributionSlice';
import { currentUserSelector } from 'modules/user/userSlice';
import { AppThunk, RootState } from 'store';
import { getPlan, plans } from 'utils/planMapper';
import { setTag, tags } from 'utils/tags';
//region types

export type Steps = 'form' | 'customInputs' | 'summary' | 'error';

interface FirstStepPayload {
  participants: number;
  days: number;
  flightLength?: FlightLengthOption;
  travellers?: number;
  roundTrip?: boolean;
}

interface SecondStepPayload {
  orderName: string;
  country: Option;
  department: string;
}

interface FullOffsetStepPayload {
  orderName?: string;
  country?: Option;
  department?: string;
}

export interface FullOffsetPeopleRow {
  category?: string;
  countryCode?: string;
  countryName?: string;
  plan: string;
  duration: number;
  participants: number;
  multiplier: number;
  price: number;
  kilosCo2: number;
}

interface EditProps {
  index: number;
  value: number;
  price?: number;
}

interface EditCategoryProps {
  index: number;
  value: string;
}

interface EditTravelClassProps {
  index: number;
  value: string;
}

interface EditRoundTripProps {
  index: number;
  value: boolean;
}

export interface FlightLinkedToFullOffset {
  flightLength: string;
  flightDistance: number;
  travelClass: TravelClass;
  roundTrip: boolean;
  travellers: number;
  kilosCo2: number;
  price: number;
}

interface OffsetWithPricePayload {
  flightKilosCo2?: number;
  eventKilosCo2: number;
  flightPrice?: number;
  eventPrice: number;
  currency: string;
}

interface OrderPayload {
  number: string;
  date: string;
}

interface CountryProps {
  index: number;
  countryName: string;
  countryCode: string;
}

interface SetCurrencyPayload {
  currency: string;
}

interface PlanProps {
  index: number;
  plan: string;
}

interface State {
  fullOffsetRows: FullOffsetPeopleRow[];
  flightsLinkedToFullOffset: FlightLinkedToFullOffset[];
  participants?: number;
  days?: number;
  flightLength?: FlightLengthOption;
  travellers?: number;
  roundTrip?: boolean;
  orderName?: string;
  country?: Option;
  department?: string;
  isLoading: boolean;
  currency?: string;
  step: Steps;
  orderNumber?: string;
  orderDate?: string;
  flightKilosCo2?: number;
  eventKilosCo2?: number;
  flightPrice?: number;
  eventPrice?: number;
}

//endregion

const initialState: State = {
  fullOffsetRows: [],
  flightsLinkedToFullOffset: [],
  isLoading: false,
  step: 'form',
};

const slice = createSlice({
  name: 'simpleOffsetEvent',
  initialState,
  reducers: {
    setFirstStep: (state: State, action: PayloadAction<FirstStepPayload>): State => {
      state.participants = action.payload.participants;
      state.days = action.payload.days;
      state.flightLength = action.payload.flightLength;
      state.travellers = action.payload.travellers;
      state.roundTrip = action.payload.roundTrip;
      return state;
    },
    setSalesCurrency: (state: State, action: PayloadAction<SetCurrencyPayload>): State => {
      return { ...state, ...action.payload };
    },
    setSecondStep: (state: State, action: PayloadAction<SecondStepPayload>): State => {
      return { ...state, ...action.payload };
    },
    setFullOffsetStep: (state: State, action: PayloadAction<FullOffsetStepPayload>): State => {
      return { ...state, ...action.payload, step: 'summary' };
    },
    setOffsetWithPrice: (state: State, action: PayloadAction<OffsetWithPricePayload>): State => {
      return { ...state, ...action.payload };
    },
    reset: (): State => {
      return { ...initialState };
    },
    resetWithoutStep: (state: State): State => {
      return { ...initialState, step: state.step };
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.isLoading = action.payload;
      return state;
    },
    setStep: (state: State, action: PayloadAction<Steps>): State => {
      state.step = action.payload;
      return state;
    },
    setOrder: (state: State, action: PayloadAction<OrderPayload>): State => {
      state.orderNumber = action.payload.number;
      state.orderDate = action.payload.date;
      return state;
    },
    updateItemParticipantsState: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value } = action.payload;
      const item = state.fullOffsetRows[index];
      item.participants = value;
      return state;
    },
    updateItemCategory: (state: State, action: PayloadAction<EditCategoryProps>): State => {
      const { index, value } = action.payload;
      const item = state.fullOffsetRows[index];
      item.category = value;
      return state;
    },
    updateItemDurationState: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value } = action.payload;
      const item = state.fullOffsetRows[index];
      item.duration = value;
      return state;
    },

    updateItemCO2: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value, price } = action.payload;
      const item = state.fullOffsetRows[index];
      item.kilosCo2 = value;
      item.price = price || 0;
      return state;
    },

    addRow: (state: State, action: PayloadAction<FullOffsetPeopleRow>): State => {
      const item = {
        category: action.payload.category || 'Crew',
        countryCode: action.payload.countryCode || 'NO',
        countryName: action.payload.countryName || 'Norway',
        plan: action.payload.plan || plans[0].id,
        duration: action.payload.duration || 1,
        participants: action.payload.participants || 1,
        kilosCo2: action.payload.kilosCo2 || 1,
        multiplier: getPlan(action.payload.plan)?.multiplier,
        price: action.payload.price || 0,
      };
      state.fullOffsetRows.push(item);
      return state;
    },
    addLinkedFlightRow: (state: State, action: PayloadAction<FlightLinkedToFullOffset>): State => {
      state.flightsLinkedToFullOffset.push(action.payload);
      return state;
    },

    updateItemCountryState: (state: State, action: PayloadAction<CountryProps>): State => {
      const { index, countryCode, countryName } = action.payload;
      const item = state.fullOffsetRows[index];
      item.countryCode = countryCode;
      item.countryName = countryName;
      return state;
    },
    updateItemPlanState: (state: State, action: PayloadAction<PlanProps>): State => {
      const { index, plan } = action.payload;
      const item = state.fullOffsetRows[index];
      item.plan = plan;
      item.multiplier = getPlan(plan)?.multiplier;
      return state;
    },

    updateFlightDistanceState: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value } = action.payload;
      const item = state.flightsLinkedToFullOffset[index];
      item.flightDistance = value;

      let flightLength = 'short';

      if (value > 1500 && value < 8000) {
        flightLength = 'medium';
      }

      if (value >= 8000 && value < 14000) {
        flightLength = 'long';
      }

      if (value >= 14000) {
        flightLength = 'ultra';
      }

      item.flightLength = flightLength;

      return state;
    },

    updateTravelClassState: (state: State, action: PayloadAction<EditTravelClassProps>): State => {
      const { index, value } = action.payload;
      const item = state.flightsLinkedToFullOffset[index];
      item.travelClass = value as TravelClass;
      return state;
    },

    updateRoundTripState: (state: State, action: PayloadAction<EditRoundTripProps>): State => {
      const { index, value } = action.payload;
      const item = state.flightsLinkedToFullOffset[index];
      item.roundTrip = value;
      return state;
    },

    updateTravellersState: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value } = action.payload;
      const item = state.flightsLinkedToFullOffset[index];
      item.travellers = value;
      return state;
    },
    updateFlightPriceState: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value } = action.payload;
      const item = state.flightsLinkedToFullOffset[index];
      item.price = value;
      return state;
    },

    updateFlightOffsetState: (state: State, action: PayloadAction<EditProps>): State => {
      const { index, value } = action.payload;
      const item = state.flightsLinkedToFullOffset[index];
      item.kilosCo2 = value;
      return state;
    },

    removeRow: (state: State, action: PayloadAction<number>): State => {
      state.fullOffsetRows.splice(action.payload, 1);
      return state;
    },

    removeLinkedFlightRow: (state: State, action: PayloadAction<number>): State => {
      state.flightsLinkedToFullOffset.splice(action.payload, 1);
      return state;
    },
  },
});

const {
  setFirstStep,
  setSecondStep,
  setLoading,
  setOffsetWithPrice,
  reset,
  setStep,
  setOrder,
  resetWithoutStep,
  addRow,
  removeRow,
  updateItemCountryState,
  updateItemPlanState,
  updateItemCO2,
  updateItemParticipantsState,
  updateItemDurationState,
  updateItemCategory,
  setSalesCurrency,
  addLinkedFlightRow,
  removeLinkedFlightRow,
  updateFlightDistanceState,
  updateTravelClassState,
  updateRoundTripState,
  updateTravellersState,
  updateFlightPriceState,
  updateFlightOffsetState,
  setFullOffsetStep,
} = slice.actions;

export {
  setFirstStep,
  setSecondStep,
  setOffsetWithPrice,
  reset,
  setStep,
  addRow,
  removeRow,
  updateItemCategory,
  removeLinkedFlightRow,
  setFullOffsetStep,
};
export default slice.reducer;

interface OffsetPriceParams {
  people: number;
  days: number;
  travellers?: number;
  flightLength?: FlightLengthOption;
  roundTrip?: boolean;
}

export const addNewLinkedFlightItem = (props?: FlightLinkedToFullOffset): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const eventOffsetApi = createEventApi(context);
  const flightOffsetApi = createFlightApi(context);
  const defaultPortfolio = defaultPortfolioSelector(getState());

  const item: FlightLinkedToFullOffset = {
    flightLength: 'short',
    flightDistance: 1500,
    travelClass: 'economy' as TravelClass,
    roundTrip: false,
    travellers: 1,
    price: 0,
    kilosCo2: 0,
  };

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers: item.travellers,
      distance: item.flightDistance,
      travelClass: item.travelClass,
      roundTrip: item.roundTrip,
    });
    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;

    const flightPrice = await eventOffsetApi.getOffsetEventPrice(
      { kilosCo2: kilosCo2, eventFlightPax: 1 },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    item.kilosCo2 = kilosCo2;
    item.price = flightPrice.salesPrice;

    dispatch(setSalesCurrency({ currency: flightPrice.salesCurrency }));
    dispatch(addLinkedFlightRow({ ...props, ...item }));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const addNewFullOffsetItem = (props?: FullOffsetPeopleRow): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const eventOffsetApi = createEventApi(context);
  const countryCode = props?.countryCode ?? 'NO';
  const participants = props?.participants ?? 1;
  const plan = getPlan(props?.plan);
  const days = props?.duration ?? 1;
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const footprint = await eventOffsetApi.getPercapitaFootprint(days, participants, countryCode);

    const kilosCo2 = footprint.kilosCo2e ?? footprint.kilosCo2;
    const multiplier = plan.multiplier;

    const { salesPrice, salesCurrency } = await eventOffsetApi.getOffsetEventPrice(
      {
        kilosCo2: kilosCo2 * multiplier,
        eventPersons: participants,
      },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    dispatch(setSalesCurrency({ currency: salesCurrency }));

    dispatch(
      addRow({
        ...props,
        multiplier,
        price: salesPrice,
        kilosCo2: kilosCo2,
        plan: plan.id,
        participants,
        duration: days,
      }),
    );
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
  }
};

export const updateItemPlan = (props: PlanProps): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const eventOffsetApi = createEventApi(context);
  const { index, plan } = props;

  const { fullOffsetRows } = getState().simpleEventOffset;
  const item = fullOffsetRows[index];
  const multiplier = getPlan(plan).multiplier;
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const footprint = await eventOffsetApi.getPercapitaFootprint(item.duration, item.participants, item.countryCode);
    const kilosCo2 = footprint.kilosCo2e ?? footprint.kilosCo2;

    const { salesPrice } = await eventOffsetApi.getOffsetEventPrice(
      {
        kilosCo2: kilosCo2 * multiplier,
        eventPersons: item.participants,
      },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    dispatch(updateItemCO2({ index, value: kilosCo2 * multiplier, price: salesPrice }));
    dispatch(updateItemPlanState({ ...props }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
  }
};

export const updateItemCountry = (props: CountryProps): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const eventOffsetApi = createEventApi(context);
  const { index, countryCode } = props;

  const { fullOffsetRows } = getState().simpleEventOffset;
  const item = fullOffsetRows[index];
  const multiplier = getPlan(item.plan).multiplier;
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const footprint = await eventOffsetApi.getPercapitaFootprint(item.duration, item.participants, countryCode);

    const kilosCo2 = footprint.kilosCo2e ?? footprint.kilosCo2;

    const { salesPrice } = await eventOffsetApi.getOffsetEventPrice(
      {
        kilosCo2: kilosCo2 * multiplier,
        eventPersons: item.participants,
      },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );
    dispatch(updateItemCO2({ index: props.index, value: kilosCo2 * multiplier, price: salesPrice }));
    dispatch(updateItemCountryState({ ...props }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
  }
};

export const getOffsetPrice = ({ people, days, travellers, flightLength, roundTrip }: OffsetPriceParams): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const eventOffsetApi = createEventApi(context);
  const flightOffsetApi = createFlightApi(context);
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const offset = await eventOffsetApi.getPercapitaFootprint(days, people);
    const kilosCo2 = offset.kilosCo2e ?? offset.kilosCo2;

    const eventPrice = await eventOffsetApi.getOffsetEventPrice(
      { kilosCo2, eventPersons: Number(people) },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    let flightPrice;
    let eventFlightPax;
    if (flightLength && travellers) {
      const result = await flightOffsetApi.getFlightOffset({
        travellers,
        distance: flightLength.distance,
        travelClass: 'economy' as TravelClass,
        roundTrip: !!roundTrip,
      });
      const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;

      const multiplier = roundTrip ? 2 : 1;
      eventFlightPax = multiplier * travellers;
      flightPrice = await eventOffsetApi.getOffsetEventPrice(
        { kilosCo2, eventFlightPax },
        currentUser?.customerId ?? '',
        defaultPortfolio?.portfolioData.id ?? '',
      );
    }

    dispatch(
      setOffsetWithPrice({
        flightKilosCo2: flightPrice?.kilosCo2e ?? flightPrice?.kilosCo2,
        flightPrice: flightPrice?.salesPrice,
        eventKilosCo2: eventPrice.kilosCo2e ?? eventPrice.kilosCo2,
        eventPrice: eventPrice.salesPrice,
        currency: eventPrice.salesCurrency,
      }),
    );
    dispatch(setStep('customInputs'));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setStep('error'));
    dispatch(resetWithoutStep());
  }
};

export const updateItemParticipants = (props: EditProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const { index, value } = props;
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const eventOffsetApi = createEventApi(context);
  dispatch(setLoading(true));
  const { fullOffsetRows } = getState().simpleEventOffset;
  const item = fullOffsetRows[index];
  const multiplier = getPlan(item.plan)?.multiplier;
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const footprint = await eventOffsetApi.getPercapitaFootprint(item.duration, value, item.countryCode);
    const kilosCo2 = footprint.kilosCo2e ?? footprint.kilosCo2;

    const { salesPrice } = await eventOffsetApi.getOffsetEventPrice(
      {
        kilosCo2: kilosCo2 * multiplier,
        eventPersons: value,
      },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id,
    );

    dispatch(updateItemCO2({ index, value: kilosCo2 * multiplier, price: salesPrice }));
    dispatch(updateItemParticipantsState({ index, value: Number(value) }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
  }
};

export const updateItemDuration = (props: EditProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const { index, value } = props;
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const eventOffsetApi = createEventApi(context);
  dispatch(setLoading(true));
  const { fullOffsetRows } = getState().simpleEventOffset;
  const item = fullOffsetRows[index];
  const multiplier = getPlan(item.plan)?.multiplier;
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const footprint = await eventOffsetApi.getPercapitaFootprint(value, item.participants, item.countryCode);

    const kilosCo2 = footprint.kilosCo2e ?? footprint.kilosCo2;

    const { salesPrice } = await eventOffsetApi.getOffsetEventPrice(
      {
        kilosCo2: kilosCo2 * multiplier,
        eventPersons: item.participants,
      },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    dispatch(updateItemCO2({ index, value: kilosCo2 * multiplier, price: salesPrice }));
    dispatch(updateItemDurationState({ index, value: Number(value) }));
    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
  }
};

export const updateFlightDistance = (props: EditProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const { index, value } = props;
  dispatch(setLoading(true));
  const context = appContextSelector(getState());
  const eventOffsetApi = createEventApi(context);
  const flightOffsetApi = createFlightApi(context);
  const currentUser = currentUserSelector(getState());
  const { flightsLinkedToFullOffset } = getState().simpleEventOffset;
  const item = flightsLinkedToFullOffset[index];
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers: item.travellers,
      distance: value,
      travelClass: item.travelClass,
      roundTrip: item.roundTrip,
    });

    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    const multiplier = item.roundTrip ? 2 : 1;

    const flightPrice = await eventOffsetApi.getOffsetEventPrice(
      { kilosCo2, eventFlightPax: item.travellers * multiplier },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id,
    );

    dispatch(updateFlightOffsetState({ index, value: kilosCo2 }));
    dispatch(updateFlightPriceState({ index, value: flightPrice.salesPrice }));
    dispatch(updateFlightDistanceState(props));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const updateTravelClass = (props: EditTravelClassProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const { index, value } = props;
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const eventOffsetApi = createEventApi(context);
  const flightOffsetApi = createFlightApi(context);
  const { flightsLinkedToFullOffset } = getState().simpleEventOffset;
  const item = flightsLinkedToFullOffset[index];
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers: item.travellers,
      distance: item.flightDistance,
      travelClass: value as TravelClass,
      roundTrip: item.roundTrip,
    });

    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    const multiplier = item.roundTrip ? 2 : 1;

    const flightPrice = await eventOffsetApi.getOffsetEventPrice(
      { kilosCo2, eventFlightPax: item.travellers * multiplier },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    dispatch(updateFlightOffsetState({ index, value: kilosCo2 }));
    dispatch(updateFlightPriceState({ index, value: flightPrice.salesPrice }));
    dispatch(updateTravelClassState(props));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const updateRoundTrip = (props: EditRoundTripProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const { index, value } = props;
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const eventOffsetApi = createEventApi(context);
  const flightOffsetApi = createFlightApi(context);
  const { flightsLinkedToFullOffset } = getState().simpleEventOffset;
  const item = flightsLinkedToFullOffset[index];
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers: item.travellers,
      distance: item.flightDistance,
      travelClass: item.travelClass,
      roundTrip: value,
    });

    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    const multiplier = value ? 2 : 1;

    const flightPrice = await eventOffsetApi.getOffsetEventPrice(
      { kilosCo2, eventFlightPax: item.travellers * multiplier },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id ?? '',
    );

    dispatch(updateFlightOffsetState({ index, value: kilosCo2 }));
    dispatch(updateFlightPriceState({ index, value: flightPrice.salesPrice }));
    dispatch(updateRoundTripState(props));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const updateTravellers = (props: EditProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const { index, value } = props;
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const eventOffsetApi = createEventApi(context);
  const flightOffsetApi = createFlightApi(context);
  const { flightsLinkedToFullOffset } = getState().simpleEventOffset;
  const item = flightsLinkedToFullOffset[index];
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const result = await flightOffsetApi.getFlightOffset({
      travellers: value,
      distance: item.flightDistance,
      travelClass: item.travelClass,
      roundTrip: item.roundTrip,
    });

    const kilosCo2 = result.kilosCo2e ?? result.kilosCo2;
    const multiplier = item.roundTrip ? 2 : 1;

    const flightPrice = await eventOffsetApi.getOffsetEventPrice(
      { kilosCo2, eventFlightPax: value * multiplier },
      currentUser?.customerId ?? '',
      defaultPortfolio?.portfolioData.id,
    );

    dispatch(updateFlightOffsetState({ index, value: kilosCo2 }));
    dispatch(updateFlightPriceState({ index, value: flightPrice.salesPrice }));
    dispatch(updateTravellersState(props));
    dispatch(setLoading(false));
  } catch {
    dispatch(setLoading(false));
  }
};

export const createFullOffsetOrder = (cb: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  const { simpleEventOffset } = getState();
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);
  const defaultPortfolio = defaultPortfolioSelector(getState());

  dispatch(setLoading(true));
  try {
    const items: OrderItem[] = [];
    const customMetadata: {
      internalName: string;
      value: string;
    }[] = [];

    if (simpleEventOffset.country) {
      customMetadata.push({
        internalName: 'country',
        value: simpleEventOffset.country.value,
      });
    }

    if (simpleEventOffset.department) {
      customMetadata.push({
        internalName: 'department',
        value: simpleEventOffset.department,
      });
    }

    simpleEventOffset.fullOffsetRows.forEach(row => {
      items.push({
        type: 'impact',
        impact: 'Co2',
        kilosCo2: row.kilosCo2 ?? 0,
        price: row.price ?? 0,
        portfolioId: defaultPortfolio?.portfolioData.id ?? 'defaultco2',
        details: {
          person: {
            days: row.duration,
            personCount: row.participants,
          },
        },
        feeFactors: {
          eventPersons: Number(row.participants ?? 0),
        },
      });
    });

    if (simpleEventOffset.flightsLinkedToFullOffset.length) {
      simpleEventOffset.flightsLinkedToFullOffset.forEach(row => {
        items.push({
          type: 'impact',
          impact: 'Co2',
          kilosCo2: row.kilosCo2 ?? 0,
          price: row.price ?? 0,
          portfolioId: defaultPortfolio?.portfolioData.id ?? 'defaultco2',
          details: {
            flight: {
              roundTrip: row.roundTrip,
              flights: 1,
              passengers: row.travellers,
              travelClass: row.travelClass,
              routeLegs: [
                {
                  distanceKm: row.flightDistance ?? 0,
                },
              ],
            },
          },
          feeFactors: {
            eventFlightPax: row.roundTrip ? 2 * Number(row.travellers ?? 0) : Number(row.travellers ?? 0),
          },
        });
      });
    }

    const order = await orderApi.create(
      {
        currency: simpleEventOffset.currency ?? '',
        description: simpleEventOffset.orderName ?? '',
        comment: '',
        category: 'Events',
        items,
        payment: {
          type: 'invoice',
        },
        metadata: {
          order: {
            channel: 'portal-fulloffset',
          },
          event: {
            days: simpleEventOffset.fullOffsetRows.reduce((prev, curr) => prev + curr.duration, 0),
            personCount: simpleEventOffset.fullOffsetRows.reduce((prev, curr) => prev + curr.participants, 0),
            flightTravelersCount: simpleEventOffset.flightsLinkedToFullOffset.reduce((prev, curr) => prev + curr.travellers, 0),
          },
          custom: customMetadata,
        },
      },
      currentUser?.customerId ?? '',
    );
    setTag({
      ...tags.portalOffset.placeOrder,
      categories: [order.order.category],
      transactionId: order.orderId,
      transactionTotal: order.order.totalPrice,
      currencyCode: order.order.currency,
      transactionProducts: order.order.items.map(item => ({
        category: order.order.category,
        name: 'Event offset',
        price: item.price,
        quantity: item.kilosCo2,
        sku: 'event-offset',
      })),
    });
    dispatch(setLoading(false));
    dispatch(
      setOrder({
        number: order.order.choooseId,
        date: order.order.createdDate,
      }),
    );
    dispatch(syncAttribution());
    cb();
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setStep('error'));
    dispatch(resetWithoutStep());
  }
};

export const createOrder = (cb: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  const { simpleEventOffset } = getState();
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);
  const defaultPortfolio = defaultPortfolioSelector(getState());

  try {
    const items: OrderItem[] = [];
    const customMetadata: {
      internalName: string;
      value: string;
    }[] = [];

    if (simpleEventOffset.country) {
      customMetadata.push({
        internalName: 'country',
        value: simpleEventOffset.country.value,
      });
    }

    if (simpleEventOffset.department) {
      customMetadata.push({
        internalName: 'department',
        value: simpleEventOffset.department,
      });
    }

    items.push({
      type: 'impact',
      impact: 'Co2',
      kilosCo2: simpleEventOffset.eventKilosCo2 ?? 0,
      price: simpleEventOffset.eventPrice ?? 0,
      portfolioId: defaultPortfolio?.portfolioData.id ?? 'defaultco2',
      details: {
        person: {
          days: Number(simpleEventOffset.days),
          personCount: Number(simpleEventOffset.participants),
        },
      },
      feeFactors: {
        eventPersons: Number(simpleEventOffset.participants ?? 0),
      },
    });

    if (simpleEventOffset.flightKilosCo2 && simpleEventOffset.flightPrice) {
      const multiplier = simpleEventOffset.roundTrip ? 2 : 1;

      items.push({
        type: 'impact',
        impact: 'Co2',
        kilosCo2: simpleEventOffset.flightKilosCo2 ?? 0,
        price: simpleEventOffset.flightPrice ?? 0,
        portfolioId: defaultPortfolio?.portfolioData.id ?? 'defaultco2',
        details: {
          flight: {
            roundTrip: simpleEventOffset.roundTrip,
            flights: 1,
            passengers: Number(simpleEventOffset.travellers),
            routeLegs: [
              {
                distanceKm: Number(simpleEventOffset.flightLength?.distance ?? 0),
              },
            ],
          },
        },
        feeFactors: {
          eventFlightPax: Number(simpleEventOffset.travellers ?? 0) * multiplier,
        },
      });
    }

    const orderResult = await orderApi.create(
      {
        currency: simpleEventOffset.currency ?? '',
        description: simpleEventOffset.orderName ?? '',
        comment: '',
        category: 'Events',
        items,
        payment: {
          type: 'invoice',
        },
        metadata: {
          order: {
            channel: 'portal-quickoffset',
          },
          event: {
            days: Number(simpleEventOffset.days ?? 0),
            personCount: Number(simpleEventOffset.participants ?? 0),
            flightTravelersCount: simpleEventOffset.travellers ? Number(simpleEventOffset.travellers) : undefined,
            flightRoundTrip: simpleEventOffset.roundTrip,
            flightLength: simpleEventOffset.flightLength?.value,
          },
          custom: customMetadata,
        },
      },
      currentUser?.customerId ?? '',
    );
    setTag({
      ...tags.portalQuickOffset.placeOrder,
      categories: [orderResult.order.category],
      transactionId: orderResult.orderId,
      transactionTotal: orderResult.order.totalPrice,
      currencyCode: orderResult.order.currency,
      transactionProducts: orderResult.order.items.map(item => ({
        category: orderResult.order.category,
        name: 'Event offset',
        price: item.price,
        quantity: item.kilosCo2,
        sku: 'event-offset',
      })),
    });
    dispatch(setLoading(false));
    dispatch(
      setOrder({
        number: orderResult.orderId,
        date: orderResult.order.createdDate,
      }),
    );
    dispatch(syncAttribution());
    cb();
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(setStep('error'));
    dispatch(resetWithoutStep());
  }
};

export const fullEventOffsetTotalImpact = createSelector(
  (state: RootState) => state.simpleEventOffset.fullOffsetRows,
  items => items.reduce<number>((prev, next) => (prev += next.kilosCo2), 0),
);

export const fullEventOffsetTotalPrice = createSelector(
  (state: RootState) => state.simpleEventOffset.fullOffsetRows,
  items => items.reduce<number>((prev, next) => (prev += next.price), 0),
);

export const fullEventOffsetTotalParticipants = createSelector(
  (state: RootState) => state.simpleEventOffset.fullOffsetRows,
  items => items.reduce<number>((prev, next) => (prev += next.participants), 0),
);

export const linkedFlightsTotalImpact = createSelector(
  (state: RootState) => state.simpleEventOffset.flightsLinkedToFullOffset,
  items => items.reduce<number>((prev, next) => (prev += next.kilosCo2), 0),
);

export const linkedFlightsTotalPrice = createSelector(
  (state: RootState) => state.simpleEventOffset.flightsLinkedToFullOffset,
  items => items.reduce<number>((prev, next) => (prev += next.price), 0),
);
